import bg_5 from "@/assets/images/bg8.jpg";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import ProductDetailModal from "@/components/feature/ProductDetailModal";
import { Product, supabase } from "@/lib/supabase";
import { useEffect, useMemo, useState } from "react";
import ProductCard from "./components/ProductCard";

interface Category { id: string; name: string; }

const ProductsPage = () => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      const [{ data: prods }, { data: cats }] = await Promise.all([
        supabase.from("products").select("*").order("created_at", { ascending: false }),
        supabase.from("categories").select("id, name").order("name"),
      ]);
      setAllProducts(prods || []);
      setCategories(cats || []);
      setLoading(false);
    };
    fetchData();
  }, []);

  const allCats = useMemo(() => [{ id: "all", name: "All" }, ...categories], [categories]);

  const filtered = useMemo(() => {
    let list = allProducts;
    if (activeCategory !== "all") list = list.filter((p) => p.category === activeCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q));
    }
    return list;
  }, [allProducts, activeCategory, searchQuery]);

  const mappedProduct = selectedProduct ? {
    id: selectedProduct.id,
    title: selectedProduct.title,
    description: selectedProduct.description,
    long_description: selectedProduct.long_description || null,
    highlights: selectedProduct.highlights || null,
    price: selectedProduct.price,
    discounted_price: selectedProduct.discounted_price || null,
    image: selectedProduct.image,
    images: selectedProduct.images || [],
    badge: selectedProduct.badge || undefined,
    category: selectedProduct.category,
    purchaseOptions: selectedProduct.purchase_options || [],
  } : null;

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-white pt-20 lg:pt-24">
        <section className="relative py-20 lg:py-28 px-6 lg:px-10 bg-cover bg-center" style={{ backgroundImage: `url(${bg_5})` }}>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
          <div className="relative z-10 text-center">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-white/70">Explore</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Our Collection</h1>
            <p className="text-white/80 text-lg max-w-lg mx-auto">Curated gifts for every occasion — browse and choose how you&apos;d like to purchase</p>
          </div>
        </section>

        <section className="sticky top-16 lg:top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-6 lg:px-10 py-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row gap-4">
            <div className="relative flex-shrink-0 sm:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input type="text" placeholder="Search products..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-full bg-gray-100 border-2 border-transparent focus:border-[#3a8cbf] focus:bg-white text-sm text-gray-700 placeholder-gray-400 outline-none transition-all" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-0.5 flex-wrap">
              {allCats.map((cat) => (
                <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${activeCategory === cat.id ? "bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="w-10 h-10 border-4 border-[#3a8cbf]/20 border-t-[#3a8cbf] rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-400 mb-8">Showing <span className="font-semibold text-gray-700">{filtered.length}</span> products</p>
                {filtered.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {filtered.map((product) => (
                      <ProductCard key={product.id} product={{ id: product.id, title: product.title, description: product.description, price: `₹${product.price}`, image: product.image, images: product.images || [], category: product.category, badge: product.badge || undefined, purchaseOptions: product.purchase_options || [] }} onBuy={() => setSelectedProduct(product)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-[#5FA8D3]/10 to-[#F78FB3]/10 mx-auto mb-5">
                      <i className="ri-search-line text-3xl text-gray-300" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No products found</h3>
                    <button onClick={() => { setSearchQuery(""); setActiveCategory("all"); }} className="mt-6 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white text-sm font-medium cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Clear Filters</button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <section className="py-16 px-6 lg:px-10 bg-gradient-to-br from-[#f0f8ff] to-[#fff0f5]">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Looking for Custom Gifts?</h2>
            <p className="text-gray-500 mb-8">We create personalised gift experiences for corporates, weddings, and special occasions.</p>
            <a href="https://wa.me/+918180084435" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white font-semibold hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap">
              <i className="ri-whatsapp-line text-lg" /> Chat on WhatsApp
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <ProductDetailModal product={mappedProduct} onClose={() => setSelectedProduct(null)} />
    </>
  );
};

export default ProductsPage;
